import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import getOptionsTypeLabel from '../../utils/getOptionsTypeLabel';
import { LANGUAGE } from '../../constants';
import type { OptionsType } from '../../types/options';
import type { Language } from '../../types/common';

interface SelectOptionsTypeProps<T = string> {
  id?: string;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  isMulti?: boolean;
  value: OptionsType<T> | OptionsType<T>[] | null;
  options: OptionsType<T>[] | undefined;
  language?: Language;
  optionsFilter?: (options: OptionsType<T>[]) => OptionsType<T>[];
  onChange?: (option: OptionsType<T> | OptionsType<T>[]) => void;
}

/**
 * OptionsTypeを選択するコンポーネント。OptionsTypeを引数に取るコールバックがあれば実行する。
 * @param id? [string]
 * @param className? [string]
 * @param placeholder? [string]
 * @param isDisabled? [boolean]
 * @param isClearable? [boolean]
 * @param isMulti? [boolean]
 * @param value [OptionsType<T> | null]
 * @param options [OptionsType[] | undefined]
 * @param optionsFilter?: (options: OptionsType<T>[]) => OptionsType<T>[];
 * @param onChange? [(option: OptionsType<T> | OptionsType<T>[]) => void;]
 * @returns [React.ReactElement]
 */
const SelectOptionsType = ({
  id = 'selectOptionsType',
  className = '',
  placeholder = '選択して下さい',
  isDisabled = false,
  isClearable = false,
  isMulti = false,
  value = null,
  options = [],
  language = LANGUAGE.JA,
  optionsFilter = (options) => options,
  onChange = () => {},
}: SelectOptionsTypeProps): React.ReactElement => {
  const [selectOptions, setSelectOptions] = useState<OptionsType[]>([]);
  const [selectedOption, setSelectedOption] = useState<OptionsType | OptionsType[]>(value);
  const getOptionLabel = (option: OptionsType) => getOptionsTypeLabel(option, language);
  const getOptionValue = (option: OptionsType) => String(option.code);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);
  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  useEffect(() => {
    const filteredOptions: OptionsType[] = optionsFilter(options);
    setSelectOptions(filteredOptions);
  }, [options]);

  // EVENT HANDLER
  // ================================================================================

  // プルダウンから組織を選んだとき
  const handleSelectChange = (options) => {
    setSelectedOption(options);
    onChange(options);
  };

  // VIEW
  // ================================================================================

  return (
    <Select
      inputId={id}
      options={selectOptions}
      value={selectedOption}
      onChange={handleSelectChange}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isMulti={isMulti}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder}
      className={'react-select-container' + ' ' + className}
      classNamePrefix="react-select"
    />
  );
};

export default SelectOptionsType;
